// src/lib/qr/builders.ts
import { ContentKind } from './types';

const esc = (s: string) => s.replace(/([\\;,":])/g, '\\$1');

export function toPayload(input: ContentKind): string {
  switch (input.kind) {
    case 'text': 
      return input.text;
    
    case 'url': 
      return input.url;
    
    case 'tel': 
      return `tel:${input.to}`;
    
    case 'sms': 
      return `SMSTO:${input.to}:${input.body ?? ''}`;
    
    case 'mms': {
      const mmsData = (input as any).mms || input;
      const params = new URLSearchParams();
      if (mmsData.subject) params.set('subject', mmsData.subject);
      if (mmsData.body) params.set('body', mmsData.body);
      return `mmsto:${mmsData.to}${params.toString() ? `?${params.toString()}` : ''}`;
    }
    
    case 'whatsapp': {
      const whatsappData = (input as any).whatsapp || input;
      const params = new URLSearchParams();
      if (whatsappData.text) params.set('text', whatsappData.text);
      return `https://wa.me/${whatsappData.to}${params.toString() ? `?${params.toString()}` : ''}`;
    }
    
    case 'telegram': {
      const telegramData = (input as any).telegram || input;
      const params = new URLSearchParams();
      if (telegramData.text) params.set('text', telegramData.text);
      return `https://t.me/${telegramData.username}${params.toString() ? `?${params.toString()}` : ''}`;
    }
    
    case 'facetime': {
      const facetimeData = (input as any).facetime || input;
      const prefix = facetimeData.audio ? 'facetime-audio' : 'facetime';
      return `${prefix}:${facetimeData.to}`;
    }
    
    case 'skype': {
      const skypeData = (input as any).skype || input;
      const params = new URLSearchParams();
      if (skypeData.call) params.set('call', 'true');
      return `skype:${skypeData.username}${params.toString() ? `?${params.toString()}` : ''}`;
    }
    
    case 'sip': {
      const sipData = (input as any).sip || input;
      const params = new URLSearchParams();
      if (sipData.subject) params.set('subject', sipData.subject);
      if (sipData.body) params.set('body', sipData.body);
      return `sip:${sipData.to}${params.toString() ? `?${params.toString()}` : ''}`;
    }
    
    case 'ussd': {
      const ussdData = (input as any).ussd || input;
      // Encode # as %23 for USSD codes
      const encodedCode = ussdData.code.replace(/#/g, '%23');
      return `tel:${encodedCode}`;
    }
    
    case 'mailto': {
      const p = new URLSearchParams();
      if (input.subject) p.set('subject', input.subject);
      if (input.body) p.set('body', input.body);
      return `mailto:${input.to}${p.toString() ? `?${p.toString()}` : ''}`;
    }
    
    case 'geo': {
      const coords = `${input.lat},${input.lng}`;
      return input.query ? `geo:${coords}?q=${encodeURIComponent(input.query)}` : `geo:${coords}`;
    }
    
    case 'wifi': {
      // Handle both nested and flat structures
      const wifiData = (input as any).wifi || input;
      const { ssid, password = '', hidden = false, auth } = wifiData;
      const authType = auth === 'nopass' ? '' : auth;
      return `WIFI:S:${esc(ssid)};T:${authType};P:${esc(password)};${hidden ? 'H:true;' : ''};`;
    }
    
    case 'vcard': {
      // Handle both nested and flat structures
      const vcardData = (input as any).vcard || input;
      const v = vcardData.version ?? '3.0';
      const lines: string[] = [
        'BEGIN:VCARD', 
        `VERSION:${v}`,
        `N:${esc(vcardData.lastName ?? '')};${esc(vcardData.firstName ?? '')}`,
        `FN:${esc([vcardData.firstName, vcardData.lastName].filter(Boolean).join(' '))}`,
      ];
      
      if (vcardData.org) lines.push(`ORG:${esc(vcardData.org)}`);
      if (vcardData.title) lines.push(`TITLE:${esc(vcardData.title)}`);
      
      vcardData.tel?.forEach((t: string) => lines.push(`TEL;TYPE=CELL:${esc(t)}`));
      vcardData.email?.forEach((e: string) => lines.push(`EMAIL;INTERNET:${esc(e)}`));
      vcardData.url?.forEach((u: string) => lines.push(`URL:${esc(u)}`));
      
      vcardData.address?.forEach((a: any) =>
        lines.push(`ADR:;;${esc(a.street ?? '')};${esc(a.city ?? '')};${esc(a.region ?? '')};${esc(a.zip ?? '')};${esc(a.country ?? '')}`),
      );
      
      lines.push('END:VCARD');
      return lines.join('\n');
    }
    
    case 'calendar': {
      // Handle both nested and flat structures
      const eventData = (input as any).event || input;
      const formatDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };
      
      const lines = [
        'BEGIN:VEVENT',
        `SUMMARY:${esc(eventData.summary)}`,
        `DTSTART:${formatDate(eventData.startTime)}`,
        `DTEND:${formatDate(eventData.endTime)}`,
      ];
      
      if (eventData.description) lines.push(`DESCRIPTION:${esc(eventData.description)}`);
      if (eventData.location) lines.push(`LOCATION:${esc(eventData.location)}`);
      if (eventData.allDay) {
        lines.push('X-MICROSOFT-CDO-ALLDAYEVENT:TRUE');
      }
      
      lines.push('END:VEVENT');
      return lines.join('\n');
    }
    
    case 'bitcoin': {
      const { payment } = input;
      const params = new URLSearchParams();
      
      if (payment.amount) params.set('amount', payment.amount.toString());
      if (payment.label) params.set('label', payment.label);
      if (payment.message) params.set('message', payment.message);
      
      const query = params.toString();
      return `bitcoin:${payment.address}${query ? `?${query}` : ''}`;
    }
    
    case 'epc': {
      // EPC/SEPA SCT format (EPC QR). Minimum set: service tag, version, character set, BIC(opt), name, IBAN, amount(opt), remittance(opt).
      const { transfer } = input;
      const amt = transfer.amount ? transfer.amount.toFixed(2) : '';
      const rem = transfer.remittance ?? '';
      const currency = transfer.currency ?? 'EUR';
      
      return [
        'BCD',  // Service tag
        '002',  // Version
        '1',    // Character set: 1 = UTF-8
        'SCT',  // SEPA Credit Transfer
        '',     // BIC (optional)
        transfer.name,
        transfer.iban.replace(/\s+/g, ''),
        amt ? `${currency}${amt}` : '',
        '',     // Purpose (optional)
        rem,
      ].join('\n');
    }
    
    case 'gs1-digital-link': {
      // Already a standards-compliant URI (FNC1 not needed in plain DL URI).
      // See GS1 Digital Link. https://www.gs1.org/standards/gs1-digital-link
      return input.gs1.uri;
    }
    
    default:
      throw new Error(`Unsupported content kind: ${(input as any).kind}`);
  }
}

// Helper function to validate payload length for different QR versions
export function validatePayloadLength(payload: string, ecl: 'L' | 'M' | 'Q' | 'H' = 'M'): boolean {
  // QR Code capacity by version and error correction level
  const capacities = {
    L: [25, 47, 77, 114, 154, 195, 224, 279, 335, 395, 468, 535, 619, 667, 758, 854, 938, 1046, 1153, 1249, 1352, 1460, 1588, 1704, 1853, 1990, 2132, 2223, 2369, 2520, 2677, 2840, 3009, 3183, 3351, 3537, 3729, 3927, 4087, 4296],
    M: [20, 38, 61, 90, 122, 154, 178, 221, 262, 311, 366, 419, 483, 528, 600, 656, 734, 816, 909, 970, 1035, 1134, 1248, 1326, 1451, 1542, 1637, 1732, 1839, 1994, 2113, 2238, 2369, 2506, 2632, 2780, 2894, 3054, 3220, 3391],
    Q: [16, 29, 47, 67, 87, 108, 125, 157, 189, 221, 259, 296, 352, 376, 426, 470, 531, 574, 644, 702, 742, 823, 890, 963, 1041, 1094, 1172, 1263, 1322, 1429, 1499, 1618, 1700, 1787, 1867, 1966, 2071, 2181, 2298, 2420],
    H: [10, 20, 35, 50, 64, 84, 93, 122, 143, 174, 200, 227, 259, 283, 321, 365, 408, 452, 493, 557, 587, 640, 672, 744, 779, 864, 910, 958, 1016, 1080, 1150, 1226, 1307, 1394, 1431, 1530, 1591, 1658, 1774, 1852]
  };
  
  const maxCapacity = Math.max(...capacities[ecl]);
  return payload.length <= maxCapacity;
}

// Helper function to get recommended error correction level based on payload length
export function getRecommendedECL(payload: string): 'L' | 'M' | 'Q' | 'H' {
  if (validatePayloadLength(payload, 'L')) return 'L';
  if (validatePayloadLength(payload, 'M')) return 'M';
  if (validatePayloadLength(payload, 'Q')) return 'Q';
  return 'H';
}
